import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import './ConfirmDialog.css';

/**
 * Promise-based confirmation dialog — an in-app replacement for native window.confirm().
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete idea',
 *     message: 'Are you sure? This cannot be undone.',
 *     confirmText: 'Delete',
 *     danger: true,
 *   });
 *   if (!ok) return;
 *
 * Maps 1:1 onto the old `if (!window.confirm(msg)) return;` pattern.
 */
const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  // Fail soft: outside a provider, fall back to native confirm so callers still work.
  return ctx || ((opts) => Promise.resolve(window.confirm(opts?.message || 'Are you sure?')));
}

const DEFAULTS = {
  title: '',
  message: 'Are you sure?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  danger: false,
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { ...options } | null
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState(null);
  }, []);

  const confirm = useCallback((options = {}) => {
    setState({ ...DEFAULTS, ...options });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') settle(false);
      if (e.key === 'Enter') settle(true);
    },
    [settle],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="confirm-overlay"
          onClick={() => settle(false)}
          role="presentation"
        >
          <div
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={state.title ? 'confirm-title' : undefined}
            aria-describedby="confirm-message"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onKeyDown}
          >
            {state.title && (
              <h2 id="confirm-title" className="confirm-title">
                {state.title}
              </h2>
            )}
            <p id="confirm-message" className="confirm-message">
              {state.message}
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn confirm-btn-cancel"
                onClick={() => settle(false)}
              >
                {state.cancelText}
              </button>
              <button
                type="button"
                className={`confirm-btn ${state.danger ? 'confirm-btn-danger' : 'confirm-btn-primary'}`}
                onClick={() => settle(true)}
                autoFocus
              >
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
